/**
 * Region (country) data for the Regional trends scope.
 *
 * `code` is the ISO 3166-1 alpha-2 country code, matching the `regions: string[]`
 * field on `Trend` and the `region` query param on `GET /trends?scope=region`
 * (see `.agents/repos/root/trends/trends-how-it-works.md`).
 *
 * One exception: `EUROPE` is a pseudo-code for the whole continent — not an
 * ISO code. The backend must treat it as "any European country". The list can
 * be extended to other continents/countries without code changes elsewhere.
 */
import {device} from '#/storage'

export type Region = {
  code: string
  name: string
}

/** Default region until geolocation is wired up (matches the Figma placeholder). */
export const DEFAULT_REGION_CODE = 'SE'

/**
 * All selectable regions, sorted by display name: every European country,
 * a whole-continent "Europe" option, and the United States.
 */
export const TREND_REGIONS: Region[] = [
  {code: 'AL', name: 'Albania'},
  {code: 'AD', name: 'Andorra'},
  {code: 'AT', name: 'Austria'},
  {code: 'BE', name: 'Belgium'},
  {code: 'BA', name: 'Bosnia & Herzegovina'},
  {code: 'BG', name: 'Bulgaria'},
  {code: 'HR', name: 'Croatia'},
  {code: 'CY', name: 'Cyprus'},
  {code: 'CZ', name: 'Czechia'},
  {code: 'DK', name: 'Denmark'},
  {code: 'EE', name: 'Estonia'},
  {code: 'EUROPE', name: 'Europe'},
  {code: 'FI', name: 'Finland'},
  {code: 'FR', name: 'France'},
  {code: 'DE', name: 'Germany'},
  {code: 'GR', name: 'Greece'},
  {code: 'HU', name: 'Hungary'},
  {code: 'IS', name: 'Iceland'},
  {code: 'IE', name: 'Ireland'},
  {code: 'IT', name: 'Italy'},
  {code: 'XK', name: 'Kosovo'},
  {code: 'LV', name: 'Latvia'},
  {code: 'LI', name: 'Liechtenstein'},
  {code: 'LT', name: 'Lithuania'},
  {code: 'LU', name: 'Luxembourg'},
  {code: 'MT', name: 'Malta'},
  {code: 'MD', name: 'Moldova'},
  {code: 'MC', name: 'Monaco'},
  {code: 'ME', name: 'Montenegro'},
  {code: 'NL', name: 'Netherlands'},
  {code: 'MK', name: 'North Macedonia'},
  {code: 'NO', name: 'Norway'},
  {code: 'PL', name: 'Poland'},
  {code: 'PT', name: 'Portugal'},
  {code: 'RO', name: 'Romania'},
  {code: 'SM', name: 'San Marino'},
  {code: 'RS', name: 'Serbia'},
  {code: 'SK', name: 'Slovakia'},
  {code: 'SI', name: 'Slovenia'},
  {code: 'ES', name: 'Spain'},
  {code: 'SE', name: 'Sweden'},
  {code: 'CH', name: 'Switzerland'},
  {code: 'UA', name: 'Ukraine'},
  {code: 'GB', name: 'United Kingdom'},
  {code: 'US', name: 'United States'},
  {code: 'VA', name: 'Vatican City'},
]

const REGION_BY_CODE: Record<string, Region> = Object.fromEntries(
  TREND_REGIONS.map(c => [c.code, c]),
)

export function getRegionByCode(code: string): Region | undefined {
  return REGION_BY_CODE[code]
}

/** Whole-continent fallback when we can't resolve a region we offer. */
export const FALLBACK_REGION_CODE = 'EUROPE'

/**
 * Initial region for the trends picker, derived from the device's IP-based
 * geolocation (`mergedGeolocation.countryCode` — the same signal the app uses
 * to pick moderation authorities). A viewer in Belgium defaults to Belgium.
 * Falls back to `FALLBACK_REGION_CODE` ("Europe") when geolocation hasn't
 * resolved yet or the country isn't one we offer.
 *
 * Reads synchronously from `device` storage (populated during the boot
 * geolocation prefetch), so it works the same on web and native and can be
 * used as the lazy initial value of the region-picker state.
 */
export function getDeviceDefaultRegionCode(): string {
  const countryCode = device
    .get(['mergedGeolocation'])
    ?.countryCode?.toUpperCase()
  if (countryCode && REGION_BY_CODE[countryCode]) return countryCode
  return FALLBACK_REGION_CODE
}
